import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import {
  defineListener,
  isTransportError,
  resourceScopeTag,
  type ClientRuntimeExtra,
  type ModuleListener,
  type ModuleListenerApi,
} from '@krp/client-runtime'

import { aqsiReceiptJournalSync, receiptsApi } from '../api/receiptsApi'
import type { ReceiptsIntentState } from './types'
import { journalSyncRequested, receiptsReloadRequested } from './commands'
import { journalSyncCleared, journalSyncFailed, journalSyncFinished, journalSyncStarted } from './receiptsSlice'

type ThunkApi = ThunkDispatch<unknown, ClientRuntimeExtra, UnknownAction>

/**
 * Dispatch listener-а — настоящий dispatch стора (thunk-middleware подключён
 * приложением), но тип `ModuleListenerApi` его не обещает: сужаем явно здесь.
 */
const thunkOf = <S>(api: ModuleListenerApi<S>): ThunkApi => api.dispatch as ThunkApi

/**
 * Listeners журнала чеков: команда → собственные endpoints пакета. Состояние
 * модуля им не нужно; ошибку постановки сверки они кладут в намерение дословно.
 */
export function createReceiptsListeners<S extends { receipts: ReceiptsIntentState }>(): ModuleListener<S>[] {
  return [
    defineListener<S, ReturnType<typeof receiptsReloadRequested>>(receiptsReloadRequested.match, (action, api) => {
      api.dispatch(receiptsApi.util.invalidateTags([resourceScopeTag(action.payload.resourceId)]))
    }),
    defineListener<S, ReturnType<typeof journalSyncRequested>>(journalSyncRequested.match, async (action, api) => {
      const { resourceId } = action.payload
      if (api.getModuleState().receipts.syncing[resourceId] === true) return
      api.dispatch(journalSyncCleared({ resourceId }))
      api.dispatch(journalSyncStarted({ resourceId }))
      // track: false — ответ сверки не копится в кэше мутаций; статус читается из aqsiReceiptJournal.
      const result = await thunkOf(api)(aqsiReceiptJournalSync.initiate(action.payload, { track: false }))
      api.dispatch(journalSyncFinished({ resourceId }))
      if ('error' in result && isTransportError(result.error)) {
        api.dispatch(
          journalSyncFailed({
            resourceId,
            failure: {
              code: result.error.code,
              message: result.error.message,
              correlationId: result.error.correlationId,
            },
          }),
        )
      }
    }),
  ]
}
